// Routes.jsx
// @flow strict
import * as React from 'react';
import { useParams, useLocation, Route, Routes } from 'react-router-dom';
import type { ParsedUrl } from './services/url/parseUrl.js';
import parseUrl from './services/url/parseUrl.js';
import { UrlContextStateManager } from './services/url/context.js';
import type { Destination } from './records/Destination.js';
import type { Language } from './services/intl/context.js';
import { AI_LOCALES, MAGAZINE_LOCALES } from '../../etc/appConfig.js';
import browserScenes from './routeScenes.js';
import RouteLoadBoundary from './components/RouteLoadBoundary/index.js';

type Props = {
  buildUrl: (ParsedUrl) => string,
  isErrorPage: boolean,
  destinations: Destination[],
  language: Language,
  scenes?: { [string]: React.ComponentType<any> },
};

function EntityHandler({ scenes }) {
  const {
    PoiPage,
    PoiToursSectionPage,
    TourForwardPage,
    GroupTourPage,
    CategoryPage,
    DestinationPage,
    NotFoundPage,
  } = scenes;
  const location = useLocation();
  const route = parseUrl(`${location.pathname}${location.search}`);
  switch (route.pageType) {
    case 'poi':
      return <PoiPage poiId={route.poi} />;
    case 'poiToursSection':
      return <PoiToursSectionPage poiId={route.poi} sectionId={route.poiToursSection} />;
    case 'forwardtopartner':
      return <TourForwardPage tourId={route.tour} />;
    case 'groupTour':
      return <GroupTourPage groupId={route.group} />;
    case 'category':
      return <CategoryPage destinationId={route.destination} categoryId={Number(route.category)} />;
    case 'pois':
    case 'destination':
      return <DestinationPage destinationId={route.destination} />;
    default:
      return <NotFoundPage language={route.language} />;
  }
}

function PoiCategoryPageHandler({ scenes }) {
  const { PoiCategoryPage, NotFoundPage } = scenes;
  const { poiId } = useParams();
  if (!poiId.startsWith('p')) {
    return <NotFoundPage />;
  }
  const poiIdNumber = Number(poiId.replace(/^p/, ''));
  if (Number.isNaN(poiIdNumber)) {
    return <NotFoundPage />;
  }
  return <PoiCategoryPage poiId={poiIdNumber} />;
}

// CloudFront Error page can be loaded with valid url, e.g. /d666 (non existing destination),
// which would match destination route, so the page is rendered with only one route
const AppRoutes = ({
  buildUrl,
  isErrorPage,
  destinations,
  language,
  scenes = browserScenes,
}: Props) => {
  const {
    LandingPage,
    AiPage,
    MagazineIndexPage,
    MagazineArticlePage,
    DestinationsPage,
    ImprintPage,
    PrivacyPolicyPage,
    TermsAndConditionsPage,
    SearchResultsPage,
    AuthorPage,
    SubCategoryPage,
    NotFoundPage,
  } = scenes;
  const location = useLocation();
  const invalidRoute = parseUrl(`${location.pathname}${location.search}`).pageType === 'notfound';
  return (
    <UrlContextStateManager buildUrl={buildUrl} destinations={destinations}>
      <RouteLoadBoundary key={location.pathname} language={language}>
        <Routes>
          {!isErrorPage && !invalidRoute ? (
            <>
              <Route path="/:lang" element={<LandingPage />} />
              <Route path="/" element={<LandingPage />} />
              <Route
                path="/:lang/ai"
                element={AI_LOCALES.includes(language) ? <AiPage /> : <NotFoundPage />}
              />
              <Route
                path="/:lang/magazine"
                element={
                  MAGAZINE_LOCALES.includes(language) ? <MagazineIndexPage /> : <NotFoundPage />
                }
              />
              <Route
                path="/:lang/magazine/:slug"
                element={
                  MAGAZINE_LOCALES.includes(language) ? <MagazineArticlePage /> : <NotFoundPage />
                }
              />
              <Route path="/:lang/destinations" element={<DestinationsPage />} />
              <Route path="/:lang/imprint" element={<ImprintPage />} />
              <Route path="/:lang/privacypolicy" element={<PrivacyPolicyPage />} />
              <Route path="/:lang/termsandconditions" element={<TermsAndConditionsPage />} />
              <Route path="/:lang/search" element={<SearchResultsPage />} />
              <Route
                path="/:lang/author/:authorId"
                element={MAGAZINE_LOCALES.includes(language) ? <AuthorPage /> : <NotFoundPage />}
              />
              <Route
                path="/:lang/:destinationId/:categoryId/category"
                element={<SubCategoryPage />}
              />
              <Route
                path="/:lang/:poiId/category"
                element={<PoiCategoryPageHandler scenes={scenes} />}
              />

              <Route
                path="/:lang/:entityId/:categoryOrSection/*"
                element={<EntityHandler scenes={scenes} />}
              />
              <Route path="/:lang/:entityId/*" element={<EntityHandler scenes={scenes} />} />

              <Route path="*" element={<NotFoundPage />} />
            </>
          ) : (
            <Route path="*" element={<NotFoundPage language={language} />} />
          )}
        </Routes>
      </RouteLoadBoundary>
    </UrlContextStateManager>
  );
};

export default AppRoutes;
